import { useMutation, useQuery } from "@apollo/client";
import { Fragment, useEffect, useState } from "react";
import { Plus as PlusIcon, Search as SearchIcon } from "@tamagui/lucide-icons";
import { InsuranceStatus, type Payer } from "@medbillai/generated-graphql-types";
import { Avatar, Body3, Divider, Input, ListItem, Spinner, YStack } from "@medbillai/ui";
import { type OnboardingNavProps, OnboardingScreen } from "../../components/OnboardingScreen";
import { type OnDone, tagged } from "../../lib/flow-engine";
import { sentryService } from "../../lib/sentry";
import { useDebounced } from "../../lib/useDebounced";
import type { OnboardingIdentity } from "./types";
import { popularPayersQuery, recordInsuranceMutation, recordInsuranceStatusMutation, searchPayersQuery, upsertOnboardingIntentMutation } from "./utils/queries";
export type InsuranceOutcomes = {
  selected: {
    carrier: string;
    payerId: string | null;
  };
  uninsured: {};
  skipped: {};
};
const Outcome = tagged<InsuranceOutcomes>();
type PayerOption = Pick<Payer, "id" | "primaryPayerId" | "displayName" | "logoUrl">;

// No virtualization in the onboarding scroll region, so cap results.
const MAX_SEARCH_RESULTS = 30;

// Fade rows out at the bottom edge of the list region.
const LIST_EDGE_FADE_MASK = "linear-gradient(to bottom, black calc(100% - 20px), transparent 100%)";
const SEARCH_DEBOUNCE_MS = 250;
const V3_NAV: OnboardingNavProps = {
  showLogo: false
};
export function Insurance({
  identity,
  nav = V3_NAV,
  onDone
}: {
  /**
   * Absent when the run never passed through Profile — the carrier is then
   * recorded without a Member row.
   */
  identity?: OnboardingIdentity;
  nav?: OnboardingNavProps;
  onDone: OnDone<InsuranceOutcomes>;
}) {
  const [searchText, setSearchText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [namingCarrier, setNamingCarrier] = useState(false);
  const query = useDebounced(searchText.trim(), SEARCH_DEBOUNCE_MS);
  const popular = useQuery(popularPayersQuery);
  const search = useQuery(searchPayersQuery, {
    variables: {
      input: {
        query,
        limit: MAX_SEARCH_RESULTS
      }
    },
    skip: query === ""
  });
  const [recordInsurance] = useMutation(recordInsuranceMutation);
  const [recordInsuranceStatus] = useMutation(recordInsuranceStatusMutation);
  const [upsertSegmentation] = useMutation(upsertOnboardingIntentMutation);
  const isSearching = searchText.trim() !== "";
  const searchPending = query !== searchText.trim() || search.loading;
  // The last results this search session settled on, so they stay on screen
  // while the next query loads. Cleared with the search box, so an earlier
  // session's results never show under a new search.
  const [settled, setSettled] = useState<readonly PayerOption[] | null>(null);
  useEffect(() => {
    if (!isSearching) {
      setSettled(null);
    } else if (!searchPending && search.data) {
      setSettled(search.data.searchPayers);
    }
  }, [isSearching, searchPending, search.data]);
  const results: readonly PayerOption[] = isSearching ? searchPending ? settled ?? [] : search.data?.searchPayers ?? [] : popular.data?.popularPayers ?? [];
  // Rows from the previous query stay visible but can't be picked.
  const stale = isSearching && searchPending;
  const loading = isSearching ? results.length === 0 && searchPending : popular.loading;
  const listError = isSearching ? search.error : popular.error;
  const noResults = isSearching && !loading && results.length === 0 && listError == null;

  // Carrier pick is the whole insurance step (no member ID): record, then
  // advance. A typed carrier has no payer ID.
  const recordCarrier = async (carrier: string, payerId: string | null) => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    setHasError(false);
    try {
      // `hasInsurance` (read by resume derivation) is the client's own write,
      // set only after the carrier record lands — flag-before-record would let
      // resume skip this screen with no carrier captured ("false complete").
      // The flag stays best-effort; its failure must not block the flow.
      await recordInsurance({
        variables: {
          input: {
            carrier,
            payerId,
            ...identity
          }
        }
      });
      try {
        await upsertSegmentation({
          variables: {
            input: {
              hasInsurance: true
            }
          }
        });
      } catch (segmentationError) {
        console.warn("Failed to record segmentation hasInsurance", segmentationError);
      }
      onDone(Outcome.selected({
        carrier,
        payerId
      }));
    } catch (error) {
      sentryService.captureException(error);
      setHasError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // "I'm uninsured" is an answer, not a skip. Recording it lets later surfaces
  // — the financial-assistance application, a case checklist asking for a
  // member card — stop asking for a card this user does not have.
  const handleUninsured = async () => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    setHasError(false);
    try {
      await recordInsuranceStatus({
        variables: {
          input: {
            status: InsuranceStatus.UNINSURED
          }
        }
      });
    } catch (error) {
      // Advancing matters more than the record: a user who cannot get past
      // this screen is a worse outcome than one whose declaration we missed,
      // and the case checklist still offers its own way to say the same thing.
      sentryService.captureException(error);
    } finally {
      setIsSubmitting(false);
    }
    onDone(Outcome.uninsured());
  };
  if (namingCarrier) {
    return <CarrierNameStep nav={nav} initialName={searchText.trim()} isSubmitting={isSubmitting} hasError={hasError} onSubmit={carrier => void recordCarrier(carrier, null)} onBack={() => {
      setHasError(false);
      setNamingCarrier(false);
    }} />;
  }
  return <OnboardingScreen contained data-sentry-element="OnboardingScreen" data-sentry-component="Insurance" data-sentry-source-file="Insurance.tsx">
      <OnboardingScreen.Nav {...nav} data-sentry-element="OnboardingScreen.Nav" data-sentry-source-file="Insurance.tsx" />
      <OnboardingScreen.Content data-sentry-element="OnboardingScreen.Content" data-sentry-source-file="Insurance.tsx">
        <OnboardingScreen.Header alignment="left" title="Select your insurance" subtitle="We'll alert you when there are opportunities to save on new bills." data-sentry-element="OnboardingScreen.Header" data-sentry-source-file="Insurance.tsx" />
        <YStack gap="$sp2" flex={1} $platform-web={{
        minHeight: 0
      }} data-sentry-element="YStack" data-sentry-source-file="Insurance.tsx">
          <Input testID="select-insurance.search" label="Search" leftIcon={SearchIcon} value={searchText} onChangeText={setSearchText} autoCapitalize="none" autoComplete="off" autoCorrect={false} spellCheck={false} data-sentry-element="Input" data-sentry-source-file="Insurance.tsx" />
          {loading ? <YStack alignItems="center" paddingVertical="$sp4">
              <Spinner />
            </YStack> :
        // Web: the contained screen gives this region shrink pressure, so
        // it scrolls internally and the footer stays on-screen; the mask
        // fades rows out at the bottom edge. Native keeps the
        // screen-level scroll region.
        <YStack $platform-web={{
          flexGrow: 1,
          flexShrink: 1,
          flexBasis: "auto",
          minHeight: 0,
          overflowY: "auto",
          maskImage: LIST_EDGE_FADE_MASK
        }}>
              {noResults ? <Body3 color="$textSubtle" paddingVertical="$sp2">
                  No results — try a different name.
                </Body3> : null}
              {results.map((payer, index) => <Fragment key={payer.id}>
                  {index > 0 ? <Divider thin /> : null}
                  <ListItem testID={`select-insurance.payer.${payer.primaryPayerId}`} paddingHorizontal="$sp0" leftElement={<Avatar size="small" shape="square" image={payer.logoUrl ?? undefined} initials={payer.displayName.charAt(0).toUpperCase()} />} title={payer.displayName} titleNumberOfLines={2} disabled={isSubmitting} onPress={() => {
              if (!stale) {
                void recordCarrier(payer.displayName, payer.primaryPayerId);
              }
            }} />
                </Fragment>)}
              {isSearching ? <>
                  {results.length > 0 ? <Divider thin /> : null}
                  <ListItem testID="select-insurance.not-listed" paddingHorizontal="$sp0" leftElement={<Avatar size="small" shape="square" icon={PlusIcon} />} title="I don't see my insurance" disabled={isSubmitting} onPress={() => {
              setHasError(false);
              setNamingCarrier(true);
            }} />
                </> : null}
              {hasError || listError != null ? <Body3 color="$textNegative" role="alert" aria-live="polite">
                  Something went wrong, please try again.
                </Body3> : null}
            </YStack>}
        </YStack>
      </OnboardingScreen.Content>
      <OnboardingScreen.Footer data-sentry-element="OnboardingScreen.Footer" data-sentry-source-file="Insurance.tsx">
        <OnboardingScreen.SecondaryAction testID="select-insurance.uninsured"
      // Gated while a carrier write is in flight: advancing as uninsured
      // mid-write would still land the carrier record.
      disabled={isSubmitting} onPress={() => void handleUninsured()} data-sentry-element="OnboardingScreen.SecondaryAction" data-sentry-source-file="Insurance.tsx">
          I'm uninsured
        </OnboardingScreen.SecondaryAction>
        <OnboardingScreen.TertiaryAction testID="select-insurance.skip"
      // Same in-flight gate as uninsured: a skip mid-write would still
      // land the carrier record ("false complete").
      disabled={isSubmitting} onPress={() => onDone(Outcome.skipped())} data-sentry-element="OnboardingScreen.TertiaryAction" data-sentry-source-file="Insurance.tsx">
          Skip
        </OnboardingScreen.TertiaryAction>
      </OnboardingScreen.Footer>
    </OnboardingScreen>;
}
function CarrierNameStep({
  nav,
  initialName,
  isSubmitting,
  hasError,
  onSubmit,
  onBack
}: {
  nav: OnboardingNavProps;
  initialName: string;
  isSubmitting: boolean;
  hasError: boolean;
  onSubmit: (carrier: string) => void;
  onBack: () => void;
}) {
  const [name, setName] = useState(initialName);
  const carrier = name.trim();
  return <OnboardingScreen contained data-sentry-element="OnboardingScreen" data-sentry-component="CarrierNameStep" data-sentry-source-file="Insurance.tsx">
      <OnboardingScreen.Nav {...nav} data-sentry-element="OnboardingScreen.Nav" data-sentry-source-file="Insurance.tsx" />
      <OnboardingScreen.Content data-sentry-element="OnboardingScreen.Content" data-sentry-source-file="Insurance.tsx">
        <OnboardingScreen.Header alignment="left" title="What's your insurance called?" subtitle="Enter it as it appears on your insurance card." data-sentry-element="OnboardingScreen.Header" data-sentry-source-file="Insurance.tsx" />
        <Input testID="select-insurance.carrier-name" label="Insurance name" value={name} onChangeText={setName} autoFocus autoCorrect={false} data-sentry-element="Input" data-sentry-source-file="Insurance.tsx" />
      </OnboardingScreen.Content>
      <OnboardingScreen.Footer data-sentry-element="OnboardingScreen.Footer" data-sentry-source-file="Insurance.tsx">
        <OnboardingScreen.FormError message={hasError ? "Something went wrong, please try again." : null} data-sentry-element="OnboardingScreen.FormError" data-sentry-source-file="Insurance.tsx" />
        <OnboardingScreen.PrimaryAction testID="select-insurance.carrier-name.continue" variant="brand" disabled={carrier === "" || isSubmitting} loading={isSubmitting} onPress={() => onSubmit(carrier)} data-sentry-element="OnboardingScreen.PrimaryAction" data-sentry-source-file="Insurance.tsx">
          Continue
        </OnboardingScreen.PrimaryAction>
        <OnboardingScreen.TertiaryAction testID="select-insurance.carrier-name.back" disabled={isSubmitting} onPress={onBack} data-sentry-element="OnboardingScreen.TertiaryAction" data-sentry-source-file="Insurance.tsx">
          Back to search
        </OnboardingScreen.TertiaryAction>
      </OnboardingScreen.Footer>
    </OnboardingScreen>;
}